// src/patient2/PatientShell.jsx  (or keep PatientShellDemo if you want)
import React, { useState, useMemo } from "react";

import BottomNav from "../../components/patient2/global/BottomNav";

import HomeScreen from "./HomeScreen";
import ExploreScreen from "./ExploreScreen";
import MyHealthScreen from "./MyHealthScreen";
import SettingsScreen from "./SettingsScreen";

import "../../components/patient2/patient2.css"; // <-- add this for styling

// --- simple helpers for demo formatting ---
const makeApptDateLabel = (dateStr, timeStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const ds = d.toLocaleDateString("sq-AL", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    if (!timeStr) return ds;
    return `${ds} • ${timeStr}`;
  } catch {
    return "";
  }
};

export default function PatientShell() {
  const [activeTab, setActiveTab] = useState("home"); // home | explore | myhealth | settings
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");

  // --------- MOCK DATA JUST TO SEE UI ---------

  const userName = "Arsel";

  const nextAppointment = {
    id: "appt1",
    doctorName: "Jennifer Kola",
    specialty: "Kardiologji",
    dateLabel: makeApptDateLabel("2025-11-25", "10:30"),
    status: "Konfirmuar",
  };

  const recentVisits = [
    {
      id: "visit1",
      avatarUrl: "",
      initials: "JK",
      name: "Dr. Jennifer Kola",
      specialty: "Kardiologji",
      subtitle: "Vizituar 2 javë më parë",
    },
    {
      id: "visit2",
      avatarUrl: "",
      initials: "AM",
      name: "Dr. Arben Meta",
      specialty: "Ortopedi",
      subtitle: "Vizituar 1 muaj më parë",
    },
  ];

  const allDoctors = [
    {
      id: "doc1",
      avatarUrl: "",
      initials: "JK",
      name: "Dr. Jennifer Kola",
      specialty: "Kardiologji",
      locationLabel: "Tiranë • 1.2 km larg",
      rating: 4.9,
      ratingCount: 32,
      priceLabel: "Vizita nga 4.000 LEK",
      badges: ["Top rated", "Për të rritur"],
    },
    {
      id: "doc2",
      avatarUrl: "",
      initials: "AM",
      name: "Dr. Arben Meta",
      specialty: "Ortopedi",
      locationLabel: "Tiranë • 3.5 km larg",
      rating: 4.7,
      ratingCount: 18,
      priceLabel: "Vizita nga 3.500 LEK",
      badges: ["I afërt"],
    },
    {
      id: "doc3",
      avatarUrl: "",
      initials: "ED",
      name: "Dr. Elira Dervishi",
      specialty: "Dermatologji",
      locationLabel: "Durrës • 35 km larg",
      rating: 4.8,
      ratingCount: 40,
      priceLabel: "Vizita nga 3.000 LEK",
      badges: ["Për fëmijë", "Top rated"],
    },
  ];

  const specialties = useMemo(
    () => Array.from(new Set(allDoctors.map((d) => d.specialty))),
    []
  );

  const filteredDoctors = useMemo(() => {
    let list = allDoctors;
    if (selectedSpecialty) {
      list = list.filter((d) => d.specialty === selectedSpecialty);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          d.specialty.toLowerCase().includes(q) ||
          (d.locationLabel || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [allDoctors, searchTerm, selectedSpecialty]);

  const recommendedDoctors = allDoctors.slice(0, 2);

  const appointmentsUpcoming = [
    {
      id: "appt1",
      doctorName: "Jennifer Kola",
      specialty: "Kardiologji",
      dateLabel: makeApptDateLabel("2025-11-25", "10:30"),
      status: "Konfirmuar",
    },
  ];

  const appointmentsPast = [
    {
      id: "appt2",
      doctorName: "Arben Meta",
      specialty: "Ortopedi",
      dateLabel: makeApptDateLabel("2025-10-10", "09:00"),
      status: "Përfunduar",
    },
  ];

  const documents = [
    {
      id: "docA",
      title: "Analiza gjaku",
      subtitle: "Laboratori XYZ",
      dateLabel: "Ngarkuar më 12 Tetor 2025",
    },
    {
      id: "docB",
      title: "Raport EKG",
      subtitle: "Dr. Jennifer Kola",
      dateLabel: "Ngarkuar më 20 Tetor 2025",
    },
  ];

  const unreadMessages = 2;

  const reviewsPending = [
    {
      id: "revPending1",
      doctorName: "Arben Meta",
      specialty: "Ortopedi",
      dateLabel: "Vizitë më 10 Tetor 2025",
    },
  ];

  const reviewsRecent = [
    {
      id: "rev1",
      doctorName: "Jennifer Kola",
      specialty: "Kardiologji",
      comment: "Mjeke shumë e kujdesshme dhe shpjeguese.",
      dateLabel: "Vlerësuar më 15 Tetor 2025",
    },
  ];

  // --------- CALLBACKS (for now just console.log) ---------

  const handleOpenDoctorProfile = (doctor) => {
    console.log("Open doctor profile:", doctor);
  };

  const handleBookDoctor = (doctor) => {
    console.log("Book doctor:", doctor);
  };

  const handleUploadDocument = () => {
    console.log("Upload document");
  };

  const handleOpenAppointment = (appt) => {
    console.log("Open appointment:", appt);
  };

  const handleOpenDocument = (doc) => {
    console.log("Open document:", doc);
  };

  const handleOpenMessages = () => {
    console.log("Open messages");
  };

  const handleWriteReview = (item) => {
    console.log("Write review for:", item);
  };

  const handleProfile = () => console.log("Go to profile");
  const handlePatientSetup = () => console.log("Go to patient setup");
  const handleNotifications = () => console.log("Open notifications");
  const handleLanguage = () => console.log("Open language settings");
  const handleLogout = () => console.log("Logout");

  // --------- RENDER CURRENT SCREEN ---------

  let screen = null;

  if (activeTab === "home") {
    screen = (
      <HomeScreen
        userName={userName}
        nextAppointment={nextAppointment}
        recentVisits={recentVisits}
        recommendedDoctors={recommendedDoctors}
        onSearch={() => setActiveTab("explore")}
        onBookAppointment={() => setActiveTab("explore")}
        onUploadDocument={() => setActiveTab("myhealth")}
        onMessages={handleOpenMessages}
        onMyDoctors={() => setActiveTab("explore")}
        onSeeAllVisits={() => setActiveTab("myhealth")}
        onSelectVisit={(visit) =>
          handleOpenDoctorProfile({ id: visit.id, name: visit.name })
        }
        onSeeAllRecommended={() => setActiveTab("explore")}
        onOpenDoctor={handleOpenDoctorProfile}
      />
    );
  } else if (activeTab === "explore") {
    screen = (
      <ExploreScreen
        searchTerm={searchTerm}
        onChangeSearch={setSearchTerm}
        onSubmitSearch={() => {}}
        specialties={specialties}
        selectedSpecialty={selectedSpecialty}
        onSelectSpecialty={setSelectedSpecialty}
        onClearFilters={() => {
          setSearchTerm("");
          setSelectedSpecialty("");
        }}
        doctors={filteredDoctors}
        onOpenDoctorProfile={handleOpenDoctorProfile}
        onBookDoctor={handleBookDoctor}
        onOpenMap={() => console.log("Open map view")}
      />
    );
  } else if (activeTab === "myhealth") {
    screen = (
      <MyHealthScreen
        appointmentsUpcoming={appointmentsUpcoming}
        appointmentsPast={appointmentsPast}
        documents={documents}
        unreadMessages={unreadMessages}
        reviewsPending={reviewsPending}
        reviewsRecent={reviewsRecent}
        onOpenAppointment={handleOpenAppointment}
        onUploadDocument={handleUploadDocument}
        onOpenDocument={handleOpenDocument}
        onOpenMessages={handleOpenMessages}
        onWriteReview={handleWriteReview}
      />
    );
  } else if (activeTab === "settings") {
    screen = (
      <SettingsScreen
        onProfile={handleProfile}
        onPatientSetup={handlePatientSetup}
        onNotifications={handleNotifications}
        onLanguage={handleLanguage}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="p2-shell-root">
      {screen}
      <BottomNav activeTab={activeTab} onChange={setActiveTab} />
    </div>
  );
}
